import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Crown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSpaceStore } from '../../stores/spaceStore';
import { useAuthStore } from '../../stores/authStore';
import { useCategoryStore } from '../../stores/categoryStore';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import BottomSheet from '../../components/ui/BottomSheet';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

interface CategoryConfig {
  id: string;
  is_fixed: boolean;
  amount_cents?: number;
  due_day?: number;
  autopay?: boolean;
  who_pays?: 'self' | 'partner' | 'alternate';
  action: 'split' | 'keep';
  split_ratio: number;
  custom_ratio_a?: number;
  custom_ratio_b?: number;
  budget_cap_cents?: number;
}

const splitRatios = [
  { label: '50/50', value: 50 },
  { label: '60/40', value: 60 },
  { label: '70/30', value: 70 },
  { label: '80/20', value: 80 },
  { label: '90/10', value: 90 }
];

const categoryEmojis = ['🛒', '🏠', '💡', '🌐', '🎬', '🎵', '📱', '🛡️', '🍽️', '🚗', '🛍️', '🎮', '💊', '✨', '🎁', '📚', '🏋️', '🐾', '🧹', '💇'];

export default function OnboardingCategories() {
  const navigate = useNavigate();
  const { currentSpace } = useSpaceStore();
  const { isPro } = useAuthStore();
  const { categories, fetchCategories, createCategory, updateCategoryDefault } = useCategoryStore();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showConfigSheet, setShowConfigSheet] = useState(false);
  const [configs, setConfigs] = useState<Record<string, CategoryConfig>>({});

  // Add custom category
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatEmoji, setNewCatEmoji] = useState('📦');
  const [newCatFixed, setNewCatFixed] = useState(false);

  useEffect(() => {
    if (!currentSpace) {
      navigate('/onboarding/space');
      return;
    }
    fetchCategories(currentSpace.id);
  }, [currentSpace]);

  useEffect(() => {
    const initialConfigs: Record<string, CategoryConfig> = {};
    categories.forEach(cat => {
      initialConfigs[cat.id] = {
        id: cat.id,
        is_fixed: cat.is_fixed,
        action: 'split',
        split_ratio: 50
      };
    });
    setConfigs(initialConfigs);
  }, [categories]);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setShowConfigSheet(true);
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) {
      toast.error('Enter a category name');
      return;
    }
    try {
      await createCategory({
        space_id: currentSpace!.id,
        name: newCatName.trim(),
        emoji: newCatEmoji,
        is_fixed: newCatFixed,
        is_custom: true
      });
      
      setConfigs(prev => {
        const updated = { ...prev };
        const newCat = categories.find(c => c.name === newCatName.trim());
        if (newCat?.id) {
          updated[newCat.id] = {
            id: newCat.id,
            is_fixed: newCatFixed,
            action: 'split',
            split_ratio: 50
          };
        }
        return updated;
      });
      
      setNewCatName('');
      setNewCatEmoji('📦');
      setNewCatFixed(false);
      setShowAddSheet(false);
      toast.success(`${newCatEmoji} ${newCatName} added!`);
      
      await fetchCategories(currentSpace!.id);
    } catch (err: any) {
      toast.error(err.message || 'Failed to add category');
    }
  };

  const updateConfig = (updates: Partial<CategoryConfig>) => {
    if (!selectedCategory) return;
    setConfigs({
      ...configs,
      [selectedCategory]: { ...configs[selectedCategory], ...updates }
    });
  };

  const handleCustomRatioA = (value: number) => {
    const b = 100 - value;
    updateConfig({ custom_ratio_a: value, custom_ratio_b: b, split_ratio: value });
  };

  const handleCustomRatioB = (value: number) => {
    const a = 100 - value;
    updateConfig({ custom_ratio_a: a, custom_ratio_b: value, split_ratio: a });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const fixedConfigs = Object.values(configs).filter(c => c.is_fixed && c.amount_cents);
      
      for (const config of fixedConfigs) {
        if (config.amount_cents && config.due_day) {
          await supabase.from('fixed_configs').insert({
            category_id: config.id,
            space_id: currentSpace!.id,
            amount_cents: config.amount_cents,
            due_day: config.due_day,
            autopay: config.autopay || false,
            alternate_turn: config.who_pays === 'alternate' ? 'member_1' : undefined
          });
        }
      }

      for (const config of Object.values(configs)) {
        await updateCategoryDefault(
          config.id,
          currentSpace!.id,
          config.action,
          config.split_ratio
        );
      }

      navigate('/onboarding/invite');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedConfig = selectedCategory ? configs[selectedCategory] : null;
  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  const members = useSpaceStore.getState().members;
  const hasPartner = members.length > 1;

  const getRatioLabel = (config: CategoryConfig) => {
    if (config.action === 'keep') return 'KEEP';
    const ratio = config.split_ratio;
    const preset = splitRatios.find(r => r.value === ratio);
    if (preset) return preset.label;
    if (config.custom_ratio_a !== undefined) return `${config.custom_ratio_a}/${config.custom_ratio_b}`;
    return `${ratio}%`;
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 flex flex-col max-w-md mx-auto w-full"
      >
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-muted/30 rounded-full" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Configure Categories</h1>
          <p className="text-muted">Tap a category to set split defaults and fixed expense settings</p>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            {categories.map(category => {
              const config = configs[category.id];
              return (
                <motion.button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  whileTap={{ scale: 0.98 }}
                  className="bg-card border border-border rounded-xl p-4 flex flex-col items-center gap-2 text-left"
                >
                  <span className="text-3xl">{category.emoji}</span>
                  <span className="font-medium text-center text-sm">{category.name}</span>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {config?.is_fixed && <Badge variant="fixed" size="sm">FIXED</Badge>}
                    <Badge variant={config?.action === 'split' ? 'split' : 'keep'} size="sm">
                      {config ? getRatioLabel(config) : '50/50'}
                    </Badge>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className="pt-6 space-y-3">
          <Button
            variant="secondary"
            onClick={() => setShowAddSheet(true)}
            className="w-full flex items-center gap-2 justify-center"
          >
            <Plus size={20} />
            Add Custom Category
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Saving...' : 'Continue'}
          </Button>
          <button
            onClick={() => navigate('/onboarding/invite')}
            className="w-full text-center text-muted text-sm hover:text-foreground touch-target"
          >
            Skip for now
          </button>
        </div>
      </motion.div>

      {/* Add Custom Category Bottom Sheet */}
      <BottomSheet
        isOpen={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        title="Add Custom Category"
      >
        <div className="space-y-6">
          {/* Emoji Picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted">Icon</label>
            <div className="grid grid-cols-10 gap-2">
              {categoryEmojis.map(e => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setNewCatEmoji(e)}
                  className={`w-full aspect-square flex items-center justify-center rounded-lg text-xl touch-target transition-colors ${
                    newCatEmoji === e ? 'bg-primary/20 ring-2 ring-primary' : 'hover:bg-muted/10'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Name Input */}
          <Input
            label="Category Name"
            placeholder="e.g., Pet Supplies, Gym"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            maxLength={50}
            autoFocus
          />

          {/* Fixed Toggle */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted">Expense Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setNewCatFixed(false)}
                className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                  !newCatFixed ? 'bg-primary text-background' : 'bg-muted/10 text-foreground'
                }`}
              >
                Variable
              </button>
              <button
                type="button"
                onClick={() => setNewCatFixed(true)}
                className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                  newCatFixed ? 'bg-primary text-background' : 'bg-muted/10 text-foreground'
                }`}
              >
                Fixed
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{newCatEmoji}</span>
              <div>
                <p className="font-medium">{newCatName || 'Category name'}</p>
                <Badge variant={newCatFixed ? 'fixed' : 'split'} size="sm">
                  {newCatFixed ? 'FIXED' : '50/50'}
                </Badge>
              </div>
            </div>
          </div>

          <Button onClick={handleAddCategory} disabled={!newCatName.trim()} className="w-full">
            Add Category
          </Button>
        </div>
      </BottomSheet>

      {/* Config Bottom Sheet */}
      <BottomSheet
        isOpen={showConfigSheet}
        onClose={() => setShowConfigSheet(false)}
        title={selectedCategoryData?.name || 'Configure'}
      >
        {selectedConfig && (
          <div className="space-y-6">
            {/* Fixed Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Expense Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => updateConfig({ is_fixed: false })}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                    !selectedConfig.is_fixed
                      ? 'bg-primary text-background'
                      : 'bg-muted/10 text-foreground'
                  }`}
                >
                  Variable
                </button>
                <button
                  type="button"
                  onClick={() => updateConfig({ is_fixed: true })}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                    selectedConfig.is_fixed
                      ? 'bg-primary text-background'
                      : 'bg-muted/10 text-foreground'
                  }`}
                >
                  Fixed
                </button>
              </div>
            </div>

            {/* Fixed Config */}
            {selectedConfig.is_fixed && (
              <div className="space-y-4 pt-4 border-t border-border">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-muted">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">$</span>
                      <input
                        type="number"
                        value={selectedConfig.amount_cents ? selectedConfig.amount_cents / 100 : ''}
                        onChange={(e) => updateConfig({ amount_cents: Math.round(parseFloat(e.target.value) * 100) || 0 })}
                        className="w-full bg-card border border-border rounded-lg py-2 pl-7 pr-3 text-foreground"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted">Due Day (1-31)</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={selectedConfig.due_day || ''}
                      onChange={(e) => updateConfig({ due_day: parseInt(e.target.value) || 1 })}
                      className="w-full bg-card border border-border rounded-lg py-2 px-3 text-foreground"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedConfig.autopay || false}
                    onChange={(e) => updateConfig({ autopay: e.target.checked })}
                    className="w-5 h-5 rounded border-border"
                  />
                  <span className="text-sm">Auto-log on due date</span>
                </label>
                {hasPartner && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted">Who Pays</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => updateConfig({ who_pays: 'self' })}
                        className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-colors ${
                          selectedConfig.who_pays === 'self' || !selectedConfig.who_pays
                            ? 'bg-primary text-background'
                            : 'bg-muted/10 text-foreground'
                        }`}
                      >
                        Self
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig({ who_pays: 'partner' })}
                        className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-colors ${
                          selectedConfig.who_pays === 'partner'
                            ? 'bg-primary text-background'
                            : 'bg-muted/10 text-foreground'
                        }`}
                      >
                        Partner
                      </button>
                      <button
                        type="button"
                        onClick={() => updateConfig({ who_pays: 'alternate' })}
                        className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-colors ${
                          selectedConfig.who_pays === 'alternate'
                            ? 'bg-primary text-background'
                            : 'bg-muted/10 text-foreground'
                        }`}
                      >
                        Alternate
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Split Options */}
            <div className="space-y-3 pt-4 border-t border-border">
              <label className="text-sm font-medium">Default Split</label>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => updateConfig({ action: 'keep' })}
                  className={`flex-1 min-w-fit py-2 px-3 rounded-xl font-medium transition-colors ${
                    selectedConfig.action === 'keep'
                      ? 'bg-keep text-white'
                      : 'bg-muted/10 text-foreground'
                  }`}
                >
                  KEEP
                </button>
                {splitRatios.map(ratio => (
                  <button
                    key={ratio.value}
                    type="button"
                    onClick={() => updateConfig({ action: 'split', split_ratio: ratio.value, custom_ratio_a: undefined, custom_ratio_b: undefined })}
                    className={`flex-1 min-w-fit py-2 px-2 rounded-xl font-medium text-sm transition-colors ${
                      selectedConfig.action === 'split' && selectedConfig.split_ratio === ratio.value && !selectedConfig.custom_ratio_a
                        ? 'bg-primary text-background'
                        : 'bg-muted/10 text-foreground'
                    }`}
                  >
                    {ratio.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => updateConfig({ action: 'split', custom_ratio_a: 55, custom_ratio_b: 45, split_ratio: 55 })}
                  className={`flex-1 min-w-fit py-2 px-2 rounded-xl font-medium text-sm transition-colors ${
                    selectedConfig.custom_ratio_a !== undefined
                      ? 'bg-primary text-background'
                      : 'bg-muted/10 text-foreground'
                  }`}
                >
                  Custom
                </button>
              </div>

              {/* Custom Ratio Inputs */}
              {selectedConfig.custom_ratio_a !== undefined && (
                <div className="bg-card rounded-xl p-3 border border-border space-y-2">
                  <label className="text-xs font-medium text-muted">Custom Split %</label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={selectedConfig.custom_ratio_a}
                        onChange={(e) => {
                          const val = Math.max(1, Math.min(99, parseInt(e.target.value) || 1));
                          handleCustomRatioA(val);
                        }}
                        className="w-full bg-background border border-border rounded-lg py-2 px-3 text-center text-foreground"
                      />
                      <p className="text-xs text-muted text-center mt-1">You</p>
                    </div>
                    <span className="text-muted">/</span>
                    <div className="flex-1">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={selectedConfig.custom_ratio_b!}
                        onChange={(e) => {
                          const val = Math.max(1, Math.min(99, parseInt(e.target.value) || 1));
                          handleCustomRatioB(val);
                        }}
                        className="w-full bg-background border border-border rounded-lg py-2 px-3 text-center text-foreground"
                      />
                      <p className="text-xs text-muted text-center mt-1">Partner</p>
                    </div>
                  </div>
                  <p className={`text-xs text-center ${
                    (selectedConfig.custom_ratio_a! + selectedConfig.custom_ratio_b!) === 100
                      ? 'text-primary'
                      : 'text-danger'
                  }`}>
                    {(selectedConfig.custom_ratio_a! + selectedConfig.custom_ratio_b!) === 100
                      ? `Each pays ${selectedConfig.custom_ratio_a}% / ${selectedConfig.custom_ratio_b}%`
                      : 'Must total 100%'}
                  </p>
                </div>
              )}
            </div>

            {/* Budget Cap (Pro-only) */}
            <div className="space-y-2 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-muted">Budget Cap (Monthly)</label>
                {!isPro && (
                  <span className="text-xs text-muted flex items-center gap-1">
                    <Crown size={12} />
                    Pro
                  </span>
                )}
              </div>
              {isPro ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">$</span>
                  <input
                    type="number"
                    value={selectedConfig.budget_cap_cents ? selectedConfig.budget_cap_cents / 100 : ''}
                    onChange={(e) => updateConfig({ budget_cap_cents: Math.round(parseFloat(e.target.value) * 100) || 0 })}
                    className="w-full bg-card border border-border rounded-lg py-2 pl-7 pr-3 text-foreground"
                    placeholder="0.00 (optional)"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toast('👑 Upgrade to Pro for budget caps', { icon: '👑' })}
                  className="w-full py-2 px-3 bg-muted/10 rounded-lg text-muted text-sm cursor-not-allowed"
                >
                  Locked — upgrade to Pro
                </button>
              )}
            </div>

            <Button
              onClick={() => setShowConfigSheet(false)}
              className="w-full"
            >
              Done
            </Button>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}
